import React, { Suspense, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Remarkable } from "remarkable";
import styled from "styled-components";
import TurndownService from "turndown";
import { ToastTypes, withToast } from "../../../contexts/toastr.context";
import { Button, FormError, Loader, TextInput } from "../../../_components";
import DropDown from "../../../_components/atoms/drop-down.component";
import TextButton from "../../../_components/atoms/text-button";
import { Radio } from "../../../_components/form-controls/radio.component";
import { Heading1 } from "../../../_components/typography";
import { ValidationErrorDiv } from "../../../_components/validation-error.component";
import { CustomPushService } from "../../../_service/custom-push.service";
const RichTextEditor = React.lazy(() => import("../../../_components/form-controls/rich-text-editor.component"));

const md = new Remarkable();
const tds = new TurndownService();

const CTA_TYPES = {
  URL: "URL",
  GROUP: "GROUP",
  SECTION: "SECTION",
};

const SELECTED_OPTION_EVERYONE = {
  value: "everyone",
  label: "Everyone",
  name: "Everyone",
};

function SendCustomPushComponent({
  user,
  community,
  groups,
  closePage,
  addToast,
  itemSelectedToPreview,
}) {
  // option list
  const [toOptions, setToOptions] = useState([SELECTED_OPTION_EVERYONE]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [buttonName, setButtonName] = useState("");
  const [buttonURL, setButtonURL] = useState("");
  const [selectedOption, setSelectedOption] = useState(toOptions[0]);

  const [ctaType, setCtaType] = useState(CTA_TYPES.URL);

  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);

  const [errors, setErrors] = useState(null);

  useEffect(() => {
    const groupLabels = groups.map((group) => ({
      value: group.id,
      label: group.name,
      name: group.name,
      sections: group.tabs.map((tab) => ({
        value: tab.id,
        label: tab.name,
        name: tab.name,
      })),
    }));
    setToOptions([
      {
        value: "everyone",
        label: "Everyone",
        name: "Everyone",
      },
      ...groupLabels,
    ]);

    setGroupOptions(groupLabels);
  }, [groups]);

  /** Preview item */
  useEffect(() => {
    if (itemSelectedToPreview) {
      console.log(itemSelectedToPreview);
      setTitle(itemSelectedToPreview.title);
      setDescription(itemSelectedToPreview.description);
      setContent(md.render(itemSelectedToPreview.content));
      setButtonName(itemSelectedToPreview.buttonName);
      setButtonURL(itemSelectedToPreview.webURL);
      setCtaType(CTA_TYPES.URL);
    } else {
      resetForm();
    }
  }, [itemSelectedToPreview]);
  // populate group based data
  useEffect(() => {
    let ctaType = CTA_TYPES.URL;
    if (itemSelectedToPreview) {
      // get to group
      if (
        itemSelectedToPreview.isCommunityWidePush ||
        !itemSelectedToPreview?.groupsPushedTo?.length
      ) {
        setSelectedOption(SELECTED_OPTION_EVERYONE);
      } else {
        // find to group
        const groupId = itemSelectedToPreview.groupsPushedTo[0];
        const selectedOption = groupOptions.find((g) => g.value === groupId);
        if (selectedOption) {
          setSelectedOption(selectedOption);
        }
      }
      // find group
      if (itemSelectedToPreview.group) {
        const selectedGroup = groupOptions.find(
          (g) => g.value === itemSelectedToPreview.group
        );
        if (selectedGroup) {
          setSelectedGroup(selectedGroup);
          ctaType = CTA_TYPES.GROUP;
          if (itemSelectedToPreview.section) {
            const selectedSection = selectedGroup.sections.find(
              (s) => s.value === itemSelectedToPreview.section
            );
            if (selectedSection) {
              ctaType = CTA_TYPES.SECTION;
              setSelectedSection(selectedSection);
            }
          }
        }
      }
      setCtaType(ctaType);
    } else {
      resetForm();
    }
  }, [itemSelectedToPreview, groupOptions]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setContent("");
    setButtonName("");
    setButtonURL("");
    setSelectedOption(SELECTED_OPTION_EVERYONE);
    setSelectedGroup(null);
    setSelectedSection(null);
    setCtaType(CTA_TYPES.URL);
  };

  const switchCTA = (ctaType) => {
    // clear all options
    setButtonURL("");
    setSelectedGroup(null);
    setSelectedSection(null);
    setCtaType(ctaType);
  };

  const checkForErrors = () => {
    const errorObj = {};
    // verify title
    if (!title) {
      errorObj.title = ["Title is required!"];
    }
    if (title.length > 50) {
      errorObj.title = ["Title should be less than 50 characters"];
    }
    // verify description
    if (description && description.length > 120) {
      errorObj.description = ["Description should be less than 120 characters"];
    }
    // content
    // buttonTitle
    if (buttonName && buttonName.length > 20) {
      errorObj.buttonName = ["Button name should be less than 20 characters"];
    }
    // cta Link
    if (ctaType === CTA_TYPES.URL && buttonURL && buttonURL.length > 240) {
      errorObj.buttonURL = ["Button URL should be less than 240 characters"];
    }
    if (ctaType === CTA_TYPES.GROUP && !selectedGroup) {
      errorObj.selectedGroup = ["Group not selected"];
    }
    if (ctaType === CTA_TYPES.SECTION) {
      if (!selectedGroup) {
        errorObj.selectedGroup = ["Group not selected"];
      }
      if (!selectedSection) {
        errorObj.selectedSection = ["Channel not selected"];
      }
    }
    // all good, clear error
    return Object.keys(errorObj).length ? errorObj : null;
  };

  const sendCustomPush = async () => {
    const errors = checkForErrors();
    setErrors(errors);
    if (errors) {
      return;
    }

    const embedlessContent = content.replace(
      /(?:<figure class="media"><oembed url=")([^"^<^>^\[^\]]+)(?:"><\/oembed><\/figure>)/g,
      " $1 "
    );

    // generate payload
    const payload = {
      title,
      description,
      content: tds.turndown(embedlessContent), // convert to md
      buttonName,
      webURL: buttonURL,
      group: selectedGroup?.value,
      section: selectedSection?.value,
      groupsPushedTo: [],
      isCommunityWidePush: false,
    };

    if (selectedOption?.value === "everyone") {
      payload.isCommunityWidePush = true;
    } else {
      payload.groupsPushedTo = [selectedOption?.value];
    }

    try {
      await CustomPushService.sendCustomPush(community.id, payload);

      addToast("Notification triggered");
    } catch (error) {
      addToast("Something went wrong", "", ToastTypes.danger);
    }
  };

  return (
    <div className="theme-bg-surface p-4 mt-0.5 flex flex-col lg:flex-row lg:space-x-4 space-y-4  lg:space-y-0">
      <div className="flex flex-col space-y-5 border theme-border-default p-4 rounded theme-bg-default w-full lg:w-1/2">
        {itemSelectedToPreview ? (
          <div className="py-2 theme-text-subtitle-2">
            Previewing an older notification
          </div>
        ) : (
          <></>
        )}
        {/* to */}
        <div className="">
          <h3 className="font-semibold ">To:</h3>
          <DropDown
            options={toOptions}
            className="w-full"
            value={selectedOption}
            onChange={(val) => {
              setSelectedOption(val);
            }}
          />
        </div>

        {/* title */}
        <div>
          <h3 className="font-semibold ">Title</h3>
          <TextInput
            inputHolderClassName="rounded theme-bg-surface"
            onChange={(val) => {
              setTitle(val);
            }}
            placeholder="Enter notification title"
            value={title}
            defaultValue={title}
            validator={(value) => {
              if (value.length > 0) {
                if (value.length > 50) {
                  return "Title should be less than 50 characters";
                }
              } else {
                return "Title is required";
              }
            }}
            errors={errors?.title}
          />
        </div>

        {/* description */}
        <div>
          <h3 className="font-semibold ">Write Description</h3>
          <textarea
            type="text"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              window.setTimeout(() => {
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }, 0);
            }}
            className="px-2 py-3 w-full flex-grow block border theme-border-default theme-bg-surface focus:outline-none"
            rows="3"
            placeholder={"Enter description here"}
          />
          <ValidationErrorDiv errors={errors} errorKey="description" />
        </div>

        {/* content */}
        <div>
          <h3 className="font-semibold ">Write Rich Text Content</h3>
          <Suspense fallback={<></>}>
            <RichTextEditor
              className=" rounded theme-bg-surface outline-none"
              setText={(e) => {
                setContent(e);
              }}
              text={content}
            />
          </Suspense>
          <ValidationErrorDiv errors={errors} errorKey="content" />
        </div>

        {/* button title */}
        <div>
          <h3 className="font-semibold ">Button Title</h3>
          <TextInput
            inputHolderClassName="rounded theme-bg-surface"
            onChange={(val) => {
              setButtonName(val);
            }}
            placeholder="Enter button title"
            value={buttonName}
            defaultValue={buttonName}
            validator={(value) => {
              if (value.length > 0) {
                if (value.length > 20) {
                  return "Button Title should be less than 20 characters";
                }
              }
            }}
            errors={errors?.buttonName}
          />
        </div>

        {/* CTA TYPE CHOOSER */}
        <div>
          <Heading1 text="CTA Type" />
          <RadioBox>
            <Radio
              selected={ctaType === CTA_TYPES.URL}
              onClick={() => {
                switchCTA(CTA_TYPES.URL);
              }}>
              URL
            </Radio>
            <Radio
              selected={ctaType === CTA_TYPES.GROUP}
              onClick={() => {
                switchCTA(CTA_TYPES.GROUP);
              }}>
              Group
            </Radio>
            <Radio
              selected={ctaType === CTA_TYPES.SECTION}
              onClick={() => {
                switchCTA(CTA_TYPES.SECTION);
              }}>
              channel
            </Radio>
          </RadioBox>
        </div>

        {/* CTA url */}
        {ctaType === CTA_TYPES.URL ? (
          <div>
            <TextInput
              inputHolderClassName="rounded theme-bg-surface"
              onChange={(val) => {
                setButtonURL(val);
              }}
              value={buttonURL}
              placeholder="https://"
              noPrefixSeparator
              defaultValue={buttonURL}
              validator={(value) => {
                if (value.length > 0) {
                  if (value.length > 240) {
                    return "Button URL should be less than 240 characters";
                  }
                }
              }}
              errors={errors?.buttonURL}
            />
          </div>
        ) : (
          <></>
        )}
        {/* cta group */}
        {ctaType === CTA_TYPES.GROUP || ctaType === CTA_TYPES.SECTION ? (
          <div className="flex">
            <div className="w-full grow-1">
              <DropDown
                options={groupOptions}
                className="w-full"
                value={selectedGroup}
                onChange={(val) => {
                  setSelectedGroup(val);
                }}
              />
              <ValidationErrorDiv errors={errors} errorKey="selectedGroup" />
            </div>
            {ctaType === CTA_TYPES.SECTION ? (
              <>
                <div className="px-4"></div>
                <div className="w-full grow-1">
                  {selectedGroup ? (
                    <>
                      <DropDown
                        options={selectedGroup.sections}
                        className="w-full"
                        value={selectedSection}
                        onChange={(val) => {
                          setSelectedSection(val);
                        }}
                      />
                      <ValidationErrorDiv
                        errors={errors}
                        errorKey="selectedSection"
                      />
                    </>
                  ) : (
                    <></>
                  )}
                </div>
              </>
            ) : (
              <></>
            )}
          </div>
        ) : (
          <></>
        )}

        {itemSelectedToPreview ? (
          <></>
        ) : (
          <>
            {/* error box */}
            <FormError
              error={errors ? "There are some errors, please check" : null}
            />

            {/* buttons */}
            <div className="flex justify-between">
              <TextButton
                label="Cancel"
                onClick={closePage}
                // isLoading={sendingMail}
              />
              <Button
                label="Send"
                // isLoading={sendingMail}
                onClick={sendCustomPush}
                disabled={title === "" || title?.length > 120}
              />
            </div>
          </>
        )}
      </div>
      {/* Notification Preview */}
      <NotificationPreview
        title={title}
        description={description}
        content={content}
        buttonName={buttonName}
        community={community}
        buttonURL={buttonURL}
      />
    </div>
  );
}

function NotificationPreview({
  title,
  description,
  content,
  buttonName,
  buttonURL,
  community,
}) {
  return (
    <div className="w-full lg:w-1/2">
      <div className="border theme-border-default p-4 theme-bg-default rounded">
        <div className="py-5">
          {/* notification preview */}
          <div className="border rounded-2xl p-4 theme-bg-surface">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <img src={community?.logo} alt="Pensil Logo" className="w-4" />
                <div className="px-2">{community?.name ?? "Pensil"}</div>
              </div>
              <div className="px-2 theme-text-subtitle-2">Now</div>
            </div>
            <div className="font-semibold pt-1">
              {title ? title : "New Notification"}
            </div>
            <div className="py-1 theme-text-subtitle-2">
              {description
                ? description
                : "Some random description jusitfying how this notification is important."}
            </div>
          </div>
          {/* divider */}
          <div className="border-b my-10"></div>
          {/* rich text preview */}
          <div>
            <div className="text-xl font-semibold py-2">
              {title ? title : "New Notification"}
            </div>
            <div className="text-sm theme-text-subtitle-2">Now</div>
            {content ? (
              <div
                className="py-2"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <div className="py-2">Rich Text Here</div>
            )}
          </div>
          {/* CTA button */}
          {buttonURL ? (
            <div className="pt-2">
              <Button
                className="w-full py-2 text-lg"
                label={buttonName ? buttonName : "Click Here"}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
}

const RadioBox = styled.div`
  padding-top: 10px;
  display: flex;
  justify-content: space-around;
`;

export default withToast(
  connect((s) => ({
    user: s.auth,
    community: s.community,
    groups: s.groups,
  }))(SendCustomPushComponent)
);
